import React, { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'operations-console-data';
const DEMO_EMAIL = 'demo-email@gmail.com';
const DEMO_PASSWORD = 'password123';

const ENTITY_CONFIG = {
  customers: {
    title: 'Customers',
    fields: [
      { name: 'name', label: 'Name', type: 'text' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'tier', label: 'Tier', type: 'text' },
    ],
  },
  orders: {
    title: 'Orders',
    fields: [
      { name: 'orderNumber', label: 'Order #', type: 'text' },
      { name: 'customer', label: 'Customer', type: 'text' },
      { name: 'status', label: 'Status', type: 'text' },
    ],
  },
  inventory: {
    title: 'Inventory',
    fields: [
      { name: 'itemName', label: 'Item Name', type: 'text' },
      { name: 'sku', label: 'SKU', type: 'text' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
    ],
  },
  users: {
    title: 'Users',
    fields: [
      { name: 'name', label: 'Name', type: 'text' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'role', label: 'Role', type: 'text' },
    ],
  },
  suppliers: {
    title: 'Suppliers',
    fields: [
      { name: 'company', label: 'Company', type: 'text' },
      { name: 'contact', label: 'Contact', type: 'text' },
      { name: 'region', label: 'Region', type: 'text' },
    ],
  },
};

const DASHBOARD_TILES = [
  { key: 'customers', title: 'Customers', type: 'entity' },
  { key: 'orders', title: 'Orders', type: 'entity' },
  { key: 'inventory', title: 'Inventory', type: 'entity' },
  { key: 'reports', title: 'Reports', type: 'report' },
  { key: 'users', title: 'Users', type: 'entity' },
  { key: 'suppliers', title: 'Suppliers', type: 'entity' },
];

const ORDER_STATUS_OPTIONS = [
  {
    value: 'Processing',
    description: 'Payment is confirmed and the warehouse is preparing the order.',
  },
  {
    value: 'Packed',
    description: 'Items are packed, labeled, and waiting for carrier pickup.',
  },
  {
    value: 'Delivered',
    description: 'The shipment reached the customer and the order is closed.',
  },
  {
    value: 'On Hold',
    description: 'The order is paused pending stock, approval, or customer response.',
  },
];

function buildSeedData() {
  return {
    customers: [
      { id: 1, name: 'Acme Retail', email: 'purchasing@acme.com', tier: 'Gold' },
      { id: 2, name: 'Northwind Cafe', email: 'owner@northwind.cafe', tier: 'Silver' },
      { id: 3, name: 'BluePeak Stores', email: 'ops@bluepeak.co', tier: 'Platinum' },
    ],
    orders: [
      { id: 1, orderNumber: 'ORD-1001', customer: 'Acme Retail', status: 'Processing' },
      { id: 2, orderNumber: 'ORD-1002', customer: 'Northwind Cafe', status: 'Packed' },
      { id: 3, orderNumber: 'ORD-1003', customer: 'BluePeak Stores', status: 'Delivered' },
    ],
    inventory: [
      { id: 1, itemName: 'Thermal Printer', sku: 'INV-TP-10', quantity: 14 },
      { id: 2, itemName: 'Barcode Scanner', sku: 'INV-BS-28', quantity: 22 },
      { id: 3, itemName: 'Shipping Labels', sku: 'INV-SL-03', quantity: 120 },
    ],
    users: [
      { id: 1, name: 'Avery Cole', email: 'avery@demo.local', role: 'Manager' },
      { id: 2, name: 'Riley Brooks', email: 'riley@demo.local', role: 'Support' },
      { id: 3, name: 'Jordan Lee', email: 'jordan@demo.local', role: 'Warehouse' },
    ],
    suppliers: [
      { id: 1, company: 'Summit Supply', contact: 'Elena Ruiz', region: 'West' },
      { id: 2, company: 'Metro Source', contact: 'Damian Fox', region: 'Central' },
      { id: 3, company: 'Harbor Goods', contact: 'Sofia Chen', region: 'East' },
    ],
  };
}

function getInitialData() {
  const stored = window.localStorage.getItem(STORAGE_KEY);

  if (!stored) {
    const seed = buildSeedData();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }

  try {
    return JSON.parse(stored);
  } catch {
    const seed = buildSeedData();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seed));
    return seed;
  }
}

function getEmptyRecord(entityKey) {
  return ENTITY_CONFIG[entityKey].fields.reduce(
    (record, field) => ({ ...record, [field.name]: '' }),
    {},
  );
}

function singularize(title) {
  return title.endsWith('s') ? title.slice(0, -1) : title;
}

function getOrderStatusOption(value) {
  return ORDER_STATUS_OPTIONS.find((option) => option.value === value) || ORDER_STATUS_OPTIONS[0];
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState(DEMO_EMAIL);
  const [loginPassword, setLoginPassword] = useState(DEMO_PASSWORD);
  const [loginError, setLoginError] = useState('');
  const [screen, setScreen] = useState('dashboard');
  const [data, setData] = useState(getInitialData);
  const [modalState, setModalState] = useState({
    open: false,
    entityKey: null,
    mode: 'add',
    record: null,
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  const reportCards = useMemo(
    () => [
      { label: 'Customers', value: data.customers.length },
      { label: 'Open Orders', value: data.orders.filter((order) => order.status !== 'Delivered').length },
      { label: 'Inventory Units', value: data.inventory.reduce((sum, item) => sum + Number(item.quantity || 0), 0) },
      { label: 'System Users', value: data.users.length },
    ],
    [data],
  );

  function handleLogin(event) {
    event.preventDefault();

    if (loginEmail === DEMO_EMAIL && loginPassword === DEMO_PASSWORD) {
      setIsAuthenticated(true);
      setLoginError('');
      return;
    }

    setLoginError('Use demo-email@gmail.com with password123 to sign in.');
  }

  function openAddModal(entityKey) {
    setModalState({
      open: true,
      entityKey,
      mode: 'add',
      record: getEmptyRecord(entityKey),
    });
  }

  function openEditModal(entityKey, record) {
    setModalState({
      open: true,
      entityKey,
      mode: 'edit',
      record,
    });
  }

  function closeModal() {
    setModalState({ open: false, entityKey: null, mode: 'add', record: null });
  }

  function saveRecord(entityKey, formData) {
    setData((currentData) => {
      const currentItems = currentData[entityKey];

      if (modalState.mode === 'edit') {
        return {
          ...currentData,
          [entityKey]: currentItems.map((item) =>
            item.id === modalState.record.id ? { ...item, ...formData, id: item.id } : item,
          ),
        };
      }

      const nextId = currentItems.reduce((maxId, item) => Math.max(maxId, item.id), 0) + 1;
      return {
        ...currentData,
        [entityKey]: [...currentItems, { ...formData, id: nextId }],
      };
    });

    closeModal();
  }

  function deleteRecord(entityKey, id) {
    setData((currentData) => ({
      ...currentData,
      [entityKey]: currentData[entityKey].filter((item) => item.id !== id),
    }));
  }

  if (!isAuthenticated) {
    return (
      <div className="app-shell login-shell">
        <div className="login-panel">
          <p className="eyebrow">Operations Console</p>
          <h1>Sign in to continue</h1>
          <p className="subtle-copy">Demo credentials are prefilled. Use them as-is to enter the app.</p>
          <form className="login-form" onSubmit={handleLogin}>
            <label>
              <span>Email</span>
              <input value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} type="email" />
            </label>
            <label>
              <span>Password</span>
              <input
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                type="password"
              />
            </label>
            {loginError ? <p className="error-text">{loginError}</p> : null}
            <button className="primary-button" type="submit">
              Log In
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Operations Console</p>
          <h1>{screen === 'dashboard' ? 'Dashboard' : screen === 'reports' ? 'Reports' : ENTITY_CONFIG[screen].title}</h1>
        </div>
        <div className="topbar-actions">
          {screen !== 'dashboard' ? (
            <button className="secondary-button" onClick={() => setScreen('dashboard')} type="button">
              Back to Dashboard
            </button>
          ) : null}
          <button
            className="secondary-button"
            onClick={() => {
              setIsAuthenticated(false);
              setScreen('dashboard');
            }}
            type="button"
          >
            Log Out
          </button>
        </div>
      </header>

      {screen === 'dashboard' ? (
        <Dashboard onOpen={setScreen} />
      ) : screen === 'reports' ? (
        <ReportsScreen reportCards={reportCards} orders={data.orders} />
      ) : (
        <EntityScreen
          entityKey={screen}
          items={data[screen]}
          onAdd={openAddModal}
          onDelete={deleteRecord}
          onEdit={openEditModal}
        />
      )}

      {modalState.open ? (
        <RecordModal modalState={modalState} onClose={closeModal} onSave={saveRecord} />
      ) : null}
    </div>
  );
}

function Dashboard({ onOpen }) {
  return (
    <main className="dashboard-grid">
      {DASHBOARD_TILES.map((tile) => (
        <button key={tile.key} className="dashboard-tile" onClick={() => onOpen(tile.key)} type="button">
          <span className="tile-kicker">Module</span>
          <strong>{tile.title}</strong>
          <span>{tile.type === 'report' ? 'View summary insights' : 'Manage records'}</span>
        </button>
      ))}
    </main>
  );
}

function EntityScreen({ entityKey, items, onAdd, onDelete, onEdit }) {
  const entity = ENTITY_CONFIG[entityKey];

  return (
    <main className="content-panel">
      <div className="panel-header">
        <div>
          <p className="eyebrow">Data Grid</p>
          <h2>{entity.title}</h2>
        </div>
        <button className="primary-button" onClick={() => onAdd(entityKey)} type="button">
          Add {singularize(entity.title)}
        </button>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {entity.fields.map((field) => (
                <th key={field.name}>{field.label}</th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                {entity.fields.map((field) => (
                  <td key={field.name}>{item[field.name]}</td>
                ))}
                <td>
                  <div className="row-actions">
                    <button className="table-button" onClick={() => onEdit(entityKey, item)} type="button">
                      Edit
                    </button>
                    <button className="table-button danger" onClick={() => onDelete(entityKey, item.id)} type="button">
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function ReportsScreen({ reportCards, orders }) {
  return (
    <main className="content-panel reports-layout">
      <div className="report-card-grid">
        {reportCards.map((card) => (
          <article key={card.label} className="report-card">
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </div>

      <section className="report-table">
        <div className="panel-header compact">
          <div>
            <p className="eyebrow">Status Summary</p>
            <h2>Recent Orders</h2>
          </div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Customer</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.orderNumber}</td>
                  <td>{order.customer}</td>
                  <td>{order.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function RecordModal({ modalState, onClose, onSave }) {
  const { entityKey, mode, record } = modalState;
  const entity = ENTITY_CONFIG[entityKey];
  const [formData, setFormData] = useState(record);

  function handleSubmit(event) {
    event.preventDefault();
    onSave(entityKey, formData);
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div className="panel-header compact">
          <div>
            <p className="eyebrow">{mode === 'add' ? 'Create Record' : 'Update Record'}</p>
            <h2>{mode === 'add' ? `Add ${singularize(entity.title)}` : `Edit ${singularize(entity.title)}`}</h2>
          </div>
          <button className="secondary-button" onClick={onClose} type="button">
            Close
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          {entity.fields.map((field) => (
            <label key={field.name}>
              <span>{field.label}</span>
              {entityKey === 'orders' && field.name === 'status' ? (
                <StatusDropdown
                  onChange={(value) =>
                    setFormData((currentFormData) => ({
                      ...currentFormData,
                      [field.name]: value,
                    }))
                  }
                  options={ORDER_STATUS_OPTIONS}
                  value={formData[field.name] || ORDER_STATUS_OPTIONS[0].value}
                />
              ) : (
                <input
                  required
                  type={field.type}
                  value={formData[field.name]}
                  onChange={(event) =>
                    setFormData((currentFormData) => ({
                      ...currentFormData,
                      [field.name]: field.type === 'number' ? Number(event.target.value) : event.target.value,
                    }))
                  }
                />
              )}
            </label>
          ))}
          <button className="primary-button" type="submit">
            {mode === 'add' ? 'Save Record' : 'Update Record'}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatusDropdown({ onChange, options, value }) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = getOrderStatusOption(value);

  return (
    <div className="status-dropdown">
      <button
        aria-expanded={isOpen}
        className="status-trigger"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        type="button"
      >
        <span className="status-copy">
          <strong>{selectedOption.value}</strong>
          <small>{selectedOption.description}</small>
        </span>
        <span className="status-caret">▾</span>
      </button>

      {isOpen ? (
        <div className="status-menu" role="listbox">
          {options.map((option) => (
            <button
              aria-selected={option.value === selectedOption.value}
              className={`status-option${option.value === selectedOption.value ? ' selected' : ''}`}
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              role="option"
              type="button"
            >
              <span className="status-copy">
                <strong>{option.value}</strong>
                <small>{option.description}</small>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default App;